import { Link } from "react-router-dom";
import Shell from "@/components/app/Shell";
import VideoEmbed from "@/components/app/VideoEmbed";
import { useOverride } from "@/context/ContentContext";
import { BookOpen, ArrowLeft, Download, ExternalLink, Sparkles } from "lucide-react";

// One combined resource — the Myo Parent Playbook PDF + a companion video titled
// "Create a Compelling Story". Both are CMS-managed under content_type=parent_playbook
// (key=main, fields pdf_url + video_url) so an admin can swap either without a code push.
// Linked from three places: Learn, FellOff, and Support. Deliberately NOT wired into
// the tribe selection / induction step — that placement is reserved for the upcoming
// Thrive Story flow.
export default function ParentPlaybook() {
  const pdfUrl = useOverride("parent_playbook", "main", "pdf_url", "");
  const videoUrl = useOverride("parent_playbook", "main", "video_url", "");

  return (
    <Shell>
      <div className="max-w-3xl mx-auto" data-testid="parent-playbook-page">
        <Link to="/learn" className="inline-flex items-center gap-2 text-sm text-ink-mid mb-6" data-testid="parent-playbook-back">
          <ArrowLeft size={14} /> All modules
        </Link>

        <div className="mb-8">
          <div className="text-xs uppercase tracking-widest text-mauve font-semibold flex items-center gap-2">
            <BookOpen size={12} /> Bonus resource
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-bold text-navy mt-1">Myo Parent Playbook</h1>
          <p className="text-ink-mid mt-3 leading-relaxed">
            Fun ideas, games, and strategies for getting your child excited and cooperative with their sessions.
            The Playbook covers what to say, how to frame practice, and small tricks that turn resistance into buy-in.
          </p>
        </div>

        {/* PDF download / open panel */}
        <section className="mb-8 bg-warm-white border border-edge rounded-3xl p-6 md:p-8" data-testid="parent-playbook-pdf-section">
          <div className="flex items-start gap-4 flex-wrap">
            <div className="w-12 h-12 rounded-2xl bg-mauve-pale text-mauve flex items-center justify-center shrink-0">
              <BookOpen size={22} />
            </div>
            <div className="flex-1 min-w-[220px]">
              <div className="text-[10px] uppercase tracking-[0.3em] text-mauve font-bold">The Playbook</div>
              <div className="font-display font-bold text-navy text-xl mt-1">Downloadable PDF</div>
              <p className="text-sm text-ink-mid mt-1 leading-relaxed">
                Print it, save it to your phone, or open it in a browser tab — refer to it any time practice starts to stall.
              </p>
            </div>
          </div>
          {pdfUrl ? (
            <div className="mt-5 flex flex-wrap gap-2">
              <a
                href={pdfUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="parent-playbook-open"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold bg-navy text-warm-white hover:bg-navy-deep"
              >
                <ExternalLink size={14} /> Open PDF
              </a>
              <a
                href={pdfUrl}
                download
                data-testid="parent-playbook-download"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold border border-edge text-navy hover:bg-cream"
              >
                <Download size={14} /> Download
              </a>
            </div>
          ) : (
            <div className="mt-5 text-xs italic text-mauve" data-testid="parent-playbook-pdf-placeholder">
              PDF link coming soon — check back after your admin uploads it.
            </div>
          )}
        </section>

        {/* Companion video */}
        <section className="mb-6" data-testid="parent-playbook-video-section">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles size={14} className="text-gold" />
            <div className="text-xs uppercase tracking-[0.3em] text-navy font-bold">Watch this together</div>
          </div>
          <h2 className="font-display text-2xl font-bold text-navy">Create a Compelling Story</h2>
          <p className="text-sm text-ink-mid mt-1 mb-4 leading-relaxed">
            A short walk-through pairing the Playbook — hear how families turn "I don't want to" into buy-in with a story worth showing up for.
          </p>
          <VideoEmbed
            value={videoUrl}
            title="Create a Compelling Story"
            placeholder="Video — Create a Compelling Story"
            testid="parent-playbook-video"
          />
        </section>
      </div>
    </Shell>
  );
}
